import { useEffect } from "react";
import { legalPages } from "@/mocks/legalPages";

/**
 * ページ単位のSEO情報（title / meta description / canonical / OGP）を
 * 1か所で解決し、DOM へ反映するための軽量フック。
 *
 * 方針：
 * - canonical は「そのページ自身の正式URL（self-canonical）」を必ず1つだけ出力する。
 * - 正式ドメインは https://sbdcl.biz を使用する（http / www / sbscl.biz は使用しない）。
 * - hash・query parameter は canonical に含めない。
 * - 旧URL（リダイレクト元）は canonical の対象にしない（そもそもルートに存在しない）。
 * - ページ本文に存在しない効果・数値・症例数などの情報は description に追加しない。
 */

/** 正式ドメイン（canonical のベースURL） */
export const SITE_ORIGIN = "https://sbdcl.biz";

/** クリニック名（サイト全体で共通の正式表記） */
const SITE_NAME = "渋谷美容皮膚科クリニック";

export type SeoConfig = {
  title: string;
  description: string;
};

/** 親カテゴリページ等の基本タイトル：ページ名｜クリニック名 */
const pageTitle = (name: string) => `${name}｜${SITE_NAME}`;

/** カテゴリ詳細ページ等のタイトル：施術名｜カテゴリ名｜クリニック名 */
const childTitle = (name: string, category: string) => `${name}｜${category}｜${SITE_NAME}`;

/** 施術詳細ページ共通の description（実際に存在するセクションのみ記載） */
const detailDescription = (name: string) =>
  `${SITE_NAME}の${name}のご案内です。施術の特徴、治療方法、料金、リスク、よくあるご質問などをご確認いただけます。`;

/**
 * ページパス → SEO 情報の定義。
 * 現行の公開ルートのみを対象とし、旧URL（リダイレクト元）は含めない。
 */
const SEO_BY_PATH: Record<string, SeoConfig> = {
  /* ------------------------------------ TOP ------------------------------------ */
  "/": {
    title: `${SITE_NAME}｜渋谷駅近くの美容皮膚科・医療脱毛`,
    description: `${SITE_NAME}は、渋谷駅近くの美容皮膚科・美容医療クリニックです。医療脱毛・医療痩身・お肌管理・再生医療まで、一人ひとりの悩みに寄り添い、納得できる美容医療をご提案します。11:00〜20:00診療。`,
  },

  /* -------------------------------- 親施術ページ6種 -------------------------------- */
  "/treatment/hair-removal": {
    title: pageTitle("医療脱毛"),
    description: `${SITE_NAME}の医療脱毛ページです。レディース・メンズ、全身・顔・VIO・ヒゲ・部位別のメニューを、医師の診察のもとご案内しています。`,
  },
  "/treatment/slim-zero": {
    title: pageTitle("医療痩身・ダイエット"),
    description: `${SITE_NAME}の医療痩身・ダイエット「Medical Slim ZERO」のご案内です。体型・部位・目的に合わせて、カウンセリングと医師の診察のもと治療方法を検討します。`,
  },
  "/treatment/skin-management": {
    title: pageTitle("お肌管理"),
    description: `${SITE_NAME}のお肌管理ページです。ニキビ・シミ・くすみ・毛穴・たるみ・肌質改善など、お悩みに合わせた施術をご案内しています。`,
  },
  "/treatment/regenerative": {
    title: pageTitle("再生医療"),
    description: `${SITE_NAME}の再生医療カテゴリのページです。幹細胞培養上清液（StemSup）を用いた点滴メニューについてご案内しています。`,
  },
  "/treatment/injectables": {
    title: pageTitle("注入治療"),
    description: `${SITE_NAME}の注入治療ページです。ボトックスとヒアルロン酸を中心に、気になる部位やお悩みに合わせた治療をご案内しています。`,
  },
  "/treatment/eye": {
    title: pageTitle("二重・目元"),
    description: `${SITE_NAME}の二重・目元ページです。二重埋没法を中心に、目元のお悩みについてご相談いただけます。`,
  },

  /* -------------------------------- お肌管理 詳細10種 -------------------------------- */
  "/skin/venus-stemsup": {
    title: childTitle("ヴィーナス ステムサップ", "お肌管理"),
    description: detailDescription("ヴィーナス ステムサップ"),
  },
  "/skin/venus-diamond": {
    title: childTitle("ヴィーナス ダイヤモンド", "お肌管理"),
    description: detailDescription("ヴィーナス ダイヤモンド"),
  },
  "/skin/cinderella-stemsup": {
    title: childTitle("シンデレラ ステムサップ", "お肌管理"),
    description: detailDescription("シンデレラ ステムサップ"),
  },
  "/skin/cinderella-diamond": {
    title: childTitle("シンデレラ ダイヤモンド", "お肌管理"),
    description: detailDescription("シンデレラ ダイヤモンド"),
  },
  "/skin/velvet-skin": {
    title: childTitle("ヴェルヴェットスキン", "お肌管理"),
    description: detailDescription("ヴェルヴェットスキン"),
  },
  "/skin/lacto-peel": {
    title: childTitle("ラクトピール", "お肌管理"),
    description: detailDescription("ラクトピール"),
  },
  "/skin/collagen-peel": {
    title: childTitle("コラーゲンピール", "お肌管理"),
    description: detailDescription("コラーゲンピール"),
  },
  "/skin/mix-peel": {
    title: childTitle("MIXピール", "お肌管理"),
    description: detailDescription("MIXピール"),
  },
  "/skin/plasma": {
    title: childTitle("プラズマ", "お肌管理"),
    description: detailDescription("プラズマ"),
  },
  "/skin/white-photo-facial": {
    title: childTitle("ホワイトフォトフェイシャル", "お肌管理"),
    description: detailDescription("ホワイトフォトフェイシャル"),
  },

  /* ------------------------------ Medical Slim ZERO 詳細4種 ------------------------------ */
  "/slim/injection": {
    title: childTitle("新脂肪溶解注射", "医療痩身・ダイエット"),
    description: detailDescription("新脂肪溶解注射"),
  },
  "/slim/rf": {
    title: childTitle("RF（ラジオ波）", "医療痩身・ダイエット"),
    description: detailDescription("RF（ラジオ波）"),
  },
  "/slim/cooling": {
    title: childTitle("脂肪冷却", "医療痩身・ダイエット"),
    description: detailDescription("脂肪冷却"),
  },
  "/slim/ems": {
    title: childTitle("医療用EMS", "医療痩身・ダイエット"),
    description: detailDescription("医療用EMS"),
  },

  /* -------------------------------- その他カテゴリ詳細 -------------------------------- */
  "/regenerative/stemsup-drip": {
    title: childTitle("StemSup点滴", "再生医療"),
    description: detailDescription("StemSup点滴"),
  },
  "/injectables/botox": {
    title: childTitle("ボトックス", "注入治療"),
    description: detailDescription("ボトックス"),
  },
  "/injectables/hyaluronic-acid": {
    title: childTitle("ヒアルロン酸", "注入治療"),
    description: detailDescription("ヒアルロン酸"),
  },
  "/eye/double-eyelid": {
    title: childTitle("二重埋没法", "二重・目元"),
    description: detailDescription("二重埋没法"),
  },

  /* ------------------------------------ 料金 ------------------------------------ */
  "/price": {
    title: pageTitle("料金表"),
    description: `${SITE_NAME}の料金表ページです。医療脱毛・医療痩身・お肌管理・再生医療・注入治療など、施術ごとの料金をご確認いただけます。`,
  },

  /* -------------------------------- 法務・採用（既存の個別設定を維持） -------------------------------- */
  "/privacy": {
    title: legalPages.privacy.title,
    description: legalPages.privacy.description,
  },
  "/minor-consent": {
    title: legalPages.minorConsent.title,
    description: legalPages.minorConsent.description,
  },
  "/medical-advertising": {
    title: legalPages.medicalAdvertising.title,
    description: legalPages.medicalAdvertising.description,
  },
  "/recruit": {
    title: legalPages.recruit.title,
    description: legalPages.recruit.description,
  },
};

/** pathname から SEO 情報を解決する（未定義のパスは TOP を返す） */
export function resolveSeo(pathname: string): SeoConfig {
  const clean = pathname.replace(/\/+$/, "") || "/";
  return SEO_BY_PATH[clean] ?? SEO_BY_PATH["/"];
}

/** pathname から self-canonical の絶対URLを組み立てる（hash / query は含めない） */
export function buildCanonicalUrl(pathname: string): string {
  const clean = pathname.replace(/\/+$/, "");
  return clean === "" ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${clean}`;
}

/** meta タグを name / property いずれかで取得または生成して content を更新する */
function setMeta(attribute: "name" | "property", key: string, content: string) {
  const existing = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  const el = existing ?? document.createElement("meta");
  if (!existing) {
    el.setAttribute(attribute, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * 与えられた SEO 情報を DOM へ反映する。
 * - canonical は既存の1つの link を更新するため、二重出力にならない。
 * - og:image / twitter:image は正式画像が無いため設定しない（架空URLを入れない）。
 */
export function useSeo({ title, description, pathname }: { title: string; description: string }) {
  useEffect(() => {
    document.title = title;

    setMeta("name", "description", description);

    const canonicalUrl = buildCanonicalUrl(pathname);
    const existingLink = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const link = existingLink ?? document.createElement("link");
    if (!existingLink) {
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonicalUrl);

    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:type", "website");

    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
  }, [title, description, pathname]);
}